<template>
  <div class="study-year-container container">
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>إدارة السنوات الدراسية</h2>
          <el-button type="primary" @click="handleCreate">
            <el-icon><Plus /></el-icon> إضافة سنة دراسية جديدة
          </el-button>
        </div>
      </template>

      <el-alert
        title="ما هي هذه الواجهة؟"
        type="info"
        description="هذه الواجهة هي حجر الأساس لتنظيم البيانات الأكاديمية. هنا، تقوم بتعريف جميع السنوات الدراسية التي ستعمل بها المدرسة (مثال: 2024-2025). يجب إضافة السنة الدراسية هنا أولاً قبل أن تتمكن من ربط الفصول أو الطلاب بها."
        :closable="false"
        show-icon
        style="margin-bottom: 20px;"
      />

      <el-table :data="studyYears" v-loading="loading" border style="width: 100%">
        <el-table-column prop="name" label="السنة الدراسية" />
        <el-table-column label="الحالة" width="120">
          <template #default="{ row }">
            <el-tag :type="row.is_active ? 'success' : 'info'">
              {{ row.is_active ? 'نشطة' : 'غير نشطة' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="الإجراءات" width="150" fixed="right">
          <template #default="{ row }">
            <el-button size="small" @click="handleEdit(row)">تعديل</el-button>
            <el-popconfirm
              title="هل أنت متأكد من حذف هذه السنة؟"
              confirm-button-text="نعم"
              cancel-button-text="لا"
              @confirm="handleDelete(row.id)"
            >
              <template #reference><el-button size="small" type="danger">حذف</el-button></template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Dialog لإنشاء/تعديل السنة -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="40%" :close-on-click-modal="false">
      <el-form :model="form" ref="formRef" label-position="top" :rules="rules">
        <el-form-item label="السنة الدراسية" prop="name">
          <el-input v-model="form.name" placeholder="مثال: 2024-2025" />
        </el-form-item>
        <el-form-item label="حالة السنة الدراسية">
          <el-switch v-model="form.is_active" active-text="نشطة" inactive-text="غير نشطة" />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">إلغاء</el-button>
          <el-button type="primary" @click="submitForm" :loading="formLoading">
            {{ form.id ? 'تحديث' : 'إنشاء' }}
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage, ElPopconfirm } from 'element-plus';
import { Plus } from '@element-plus/icons-vue';

export default {
  name: 'StudyYearManagement',
  components: { Plus, ElPopconfirm },
  data() {
    return {
      api: null,
      loading: true,
      studyYears: [],
      dialogVisible: false,
      formLoading: false,
      form: this.getInitialForm(),
      rules: {
        name: [
          { required: true, message: 'اسم السنة الدراسية مطلوب', trigger: 'blur' },
          { pattern: /^\d{4}-\d{4}$/, message: 'الرجاء إدخال تنسيق صحيح (مثال: 2024-2025)', trigger: 'blur' },
        ],
      },
    };
  },
  computed: {
    dialogTitle() {
      return this.form.id ? 'تعديل سنة دراسية' : 'إضافة سنة دراسية جديدة';
    },
  },
  created() {
    this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      return config;
    });
    this.fetchStudyYears();
  },
  methods: {
    getInitialForm() {
      return { id: null, name: '', is_active: true };
    },
    async fetchStudyYears() {
      this.loading = true;
      try {
        const response = await this.api.get('/study-years');
        this.studyYears = response.data.data || response.data; // Handle both paginated and non-paginated responses
      } catch (error) {
        ElMessage.error('فشل في جلب السنوات الدراسية.');
      } finally {
        this.loading = false;
      }
    },
    handleCreate() {
      this.form = this.getInitialForm();
      this.dialogVisible = true;
    },
    handleEdit(year) {
      this.form = { 
        id: year.id,
        name: year.name,
        is_active: year.is_active
      };
      this.dialogVisible = true;
    },
    async submitForm() {
      if (!this.$refs.formRef) return;
      await this.$refs.formRef.validate(async (valid) => {
        if (!valid) return;
        this.formLoading = true;
        try {
          if (this.form.id) {
            await this.api.put(`/study-years/${this.form.id}`, this.form);
            ElMessage.success('تم تحديث السنة بنجاح!');
          } else {
            await this.api.post('/study-years', this.form);
            ElMessage.success('تم إنشاء السنة بنجاح!');
          }
          this.dialogVisible = false;
          this.fetchStudyYears();
        } catch (error) {
          const errorMsg = error.response?.data?.errors ? Object.values(error.response.data.errors)[0][0] : (error.response?.data?.message || 'فشلت العملية.');
          ElMessage.error(errorMsg);
        } finally {
          this.formLoading = false;
        }
      });
    },
    async handleDelete(id) {
      try {
        await this.api.delete(`/study-years/${id}`);
        ElMessage.success('تم حذف السنة بنجاح!');
        this.fetchStudyYears();
      } catch (error) {
        ElMessage.error(error.response?.data?.message || 'فشل في حذف السنة.');
      }
    },
  },
};
</script>

<style scoped>
.study-year-container {
  padding: 20px;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
